import { Skeleton } from 'antd';
import { useGetProductDetailsQuery } from '../../../store/services/products';
import { useGetCategoryAttributesQuery } from '../../../store/services/lookups';
import { usePublicState } from '../../../store/slices/publicSlice';
import ProductDetailsBreadcrumbs from '../ProductDetails/ProductDetailsBreadcrumbs';
import ProductInfoSection from '../ProductDetails/ProductInfoSection';
import ProductTechnicalSpecsSection from '../ProductDetails/ProductTechnicalSpecsSection';
import ProductImagesGallery from '../ProductDetails/ProductImagesGallery';
import PricingAvailabilityCard from '../ProductDetails/PricingAvailabilityCard';
import ApproveProductModal from '../ProductDetails/Modals/ApproveProductModal';
import ReturnProductModal from '../ProductDetails/Modals/ReturnProductModal';
import RejectProductModal from '../ProductDetails/Modals/RejectProductModal';
import ActivityLogsList from '@/components/AdminLogs/IndividualLog/ActivityLogsList';
import { useParams, useSearchParams } from 'react-router-dom';


/**
 * Admin/Operator product request details.
 * Uses the operator endpoint (/operator/product-requests/:requestId)
 * which returns request-level data including the canApprove / canReject / canEdit
 * action flags consumed by ProductActions.
 *
 * The requestId (product request ID) is also forwarded to the action modals
 * so they can call the correct operator mutation endpoints.
 */
export default function AdminProductRequestContent(): JSX.Element {

  const { requestId } = useParams<{ requestId: string }>();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId') || '';

  const { data, isLoading } = useGetProductDetailsQuery(
    { productId: requestId },
    { skip: !requestId }
  );

  const basicDetails = data?.payload;

  const { data: categoryAttributesResp } = useGetCategoryAttributesQuery(
    { categoryId: basicDetails?.categoryId || '' },
    { skip: !basicDetails?.categoryId }
  );

  const { modalId } = usePublicState();

  const categoryAttributes = categoryAttributesResp?.payload || [];

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="admin-product-request-details-content"
    >
      {/* Breadcrumbs + action buttons (Approve / Reject / Edit) */}
      <ProductDetailsBreadcrumbs
        productId={requestId as string}
        basicDetails={basicDetails}
        loading={isLoading}
      />
      <div
        className="flex flex-col lg:flex-row gap-6 items-start w-full"
        data-testid="admin-product-request-details-cols-wrapper"
      >
        {/* Left Column - 60% */}
        <div
          className="w-full lg:w-3/5 flex flex-col gap-4 p-6 bg-white dark:bg-gray-900 rounded-[12px] border border-[#E7E7E9] dark:border-gray-800"
          data-testid="admin-product-request-details-left-col"
        >
          {isLoading ? (
            <Skeleton active title paragraph={{ rows: 6 }} className="mb-4" />
          ) : (
            <ProductInfoSection
              basicDetails={basicDetails}
              categoryAttributes={categoryAttributes}
            />
          )}

          {isLoading ? (
            <Skeleton active title paragraph={{ rows: 8 }} />
          ) : (
            <ProductTechnicalSpecsSection
              basicDetails={basicDetails}
              categoryAttributes={categoryAttributes}
            />
          )}

          <ActivityLogsList
            type="PRODUCT"
            id={productId}
          />
        </div>

        {/* Right Column - 40% */}
        <div
          className="w-full lg:w-2/5 flex flex-col gap-6 lg:sticky lg:top-24"
          data-testid="admin-product-request-details-right-col"
        >
          {isLoading ? (
            <Skeleton
              active
              title
              paragraph={{ rows: 6 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-800"
            />
          ) : (
            <ProductImagesGallery basicDetails={basicDetails} />
          )}

          {isLoading ? (
            <Skeleton
              active
              title
              paragraph={{ rows: 6 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-800"
            />
          ) : (
            <PricingAvailabilityCard basicDetails={basicDetails} />
          )}
        </div>
      </div>
      {/* Operator action modals — always available in this context */}
      {modalId === 'APPROVE_PRODUCT_MODAL' && <ApproveProductModal />}
      {modalId === 'RETURN_PRODUCT_MODAL' && <ReturnProductModal />}
      {modalId === 'REJECT_PRODUCT_MODAL' && <RejectProductModal />}
    </div>
  );
}
